import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import MarkdownRenderer from '../components/blog/MarkdownRenderer';
import { getBlogPostBySlug, getAllBlogPosts } from '../lib/markdown';
import type { BlogPost } from '../types/blog';
import { format } from 'date-fns';
import { zhCN } from 'date-fns/locale';

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [adjacentPosts, setAdjacentPosts] = useState<{
    prev: BlogPost | null;
    next: BlogPost | null;
  }>({ prev: null, next: null });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!slug) return;

    getBlogPostBySlug(slug).then(async (foundPost) => {
      if (!foundPost) {
        setLoading(false);
        return;
      }

      setPost(foundPost);

      // Get adjacent posts
      const allPosts = await getAllBlogPosts();
      const currentIndex = allPosts.findIndex(p => p.slug === slug);
      setAdjacentPosts({
        prev: currentIndex > 0 ? allPosts[currentIndex - 1] : null,
        next: currentIndex < allPosts.length - 1 ? allPosts[currentIndex + 1] : null,
      });

      setLoading(false);
    });
  }, [slug]);

  if (loading) {
    return (
      <div className="container-custom py-20">
        <div className="text-center text-xl">加载中...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container-custom py-20 text-center">
        <h1 className="text-4xl font-heading font-bold mb-4">文章未找到</h1>
        <p className="text-xl text-gray-600 mb-8">
          抱歉，您访问的文章不存在。
        </p>
        <Link
          to="/blog"
          className="btn-neubrutal bg-cta text-white px-6 py-3"
        >
          返回博客列表
        </Link>
      </div>
    );
  }

  return (
    <div className="container-custom py-12">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="mb-8 text-cta font-medium hover:underline flex items-center gap-2"
      >
        ← 返回
      </button>

      {/* Header */}
      <article>
        <header className="mb-8">
          <h1 className="text-5xl md:text-6xl font-heading font-bold mb-4">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-gray-600">
            <span className="text-lg">
              {format(new Date(post.date), 'yyyy年MM月dd日', { locale: zhCN })}
            </span>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 border-2 border-black bg-primary text-white rounded-full text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </header>

        {/* Cover image */}
        {post.coverImage && (
          <div className="aspect-video mb-8 bg-gradient-to-br from-secondary/20 to-primary/20 border-3 border-black shadow-neubrutal rounded-lg flex items-center justify-center">
            <span className="text-9xl">📝</span>
          </div>
        )}

        {/* Content */}
        <div className="bg-white border-3 border-black shadow-neubrutal rounded-lg p-8 md:p-12">
          <MarkdownRenderer content={post.content} />
        </div>
      </article>

      {/* Navigation */}
      <nav className="mt-12 flex justify-between gap-4">
        {adjacentPosts.prev ? (
          <Link
            to={`/blog/${adjacentPosts.prev.slug}`}
            className="card-neubrutal flex-1 p-6 rounded-lg text-center"
          >
            <div className="text-sm text-gray-600 mb-2">上一篇</div>
            <div className="text-xl font-heading font-bold">
              {adjacentPosts.prev.title}
            </div>
          </Link>
        ) : (
          <div className="flex-1" />
        )}
        {adjacentPosts.next ? (
          <Link
            to={`/blog/${adjacentPosts.next.slug}`}
            className="card-neubrutal flex-1 p-6 rounded-lg text-center"
          >
            <div className="text-sm text-gray-600 mb-2">下一篇</div>
            <div className="text-xl font-heading font-bold">
              {adjacentPosts.next.title}
            </div>
          </Link>
        ) : (
          <div className="flex-1" />
        )}
      </nav>
    </div>
  );
}
